import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ENTITY_START_Y, entityToScreen, ROW_HEIGHT, renderTimeline, type TimelineLabels } from "./renderTimeline";
import type { TimelineEntity, TimelineProps, ViewState } from "./Timeline.types";
import { TimelineMinimap } from "./TimelineMinimap";
import { TimelineZoomControls } from "./TimelineZoomControls";

// v2: coordinate system changed to identity yearToX (year = world X).
// Bumped to invalidate old viewStates calibrated to the previous normalized system.
const TIMELINE_VIEW_STATE_KEY_BASE = "dewey.timeline.viewState.v2";

const getStorageKey = (key?: string) => (key ? `${TIMELINE_VIEW_STATE_KEY_BASE}.${key}` : TIMELINE_VIEW_STATE_KEY_BASE);

const TEXT_FONT_PX = 16;
const TEXT_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, sans-serif";
const BOLD_TEXT_FONT = `bold ${TEXT_FONT_PX}px ${TEXT_FONT_FAMILY}`;
const MIN_SCALE = 0.1;
const BASE_MAX_SCALE = 50;
const MIN_ENTITY_WIDTH = 20;
const VISIBLE_RANGE_DEBOUNCE_MS = 400;

export const Timeline: React.FC<TimelineProps> = ({
  entities,
  height,
  onEntityClick,
  onEntityHover,
  storageKey,
  onVisibleYearRangeChange,
  initialViewState,
  onViewStateChange,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [actualWidth, setActualWidth] = useState(800);
  const persistKey = useMemo(() => getStorageKey(storageKey), [storageKey]);

  const labels: TimelineLabels = useMemo(
    () => ({
      startYear: (year: string) => t("feature.timeline.startYear", { year }),
      endYear: (year: string) => t("feature.timeline.endYear", { year }),
      age: (age: number) => t("feature.timeline.age", { age }),
      categories: (categories: string) => t("feature.timeline.categories", { categories }),
    }),
    [t],
  );

  const [viewState, setViewState] = useState<ViewState>(() => {
    if (initialViewState) return initialViewState;
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(persistKey);
        if (saved) return JSON.parse(saved);
      } catch {
        // ignore
      }
    }
    // Default: show years 0–2000 (scale 0.4 → 800px canvas spans 2000 years).
    // calculateFitView will override this once data loads.
    return { offsetX: 0, offsetY: 0, scale: 0.4 };
  });

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hoveredEntity, setHoveredEntity] = useState<TimelineEntity | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [initialized, setInitialized] = useState(false);

  const width = actualWidth;

  // Entity year range — used only for calculateFitView and maxScale.
  // NOT used for yearToX (stable identity function below).
  const entityYearRange = useMemo(() => {
    if (entities.length === 0) return 500;
    const years = entities.map((e) => e.start.year);
    const eMin = years.reduce((mn, y) => (y < mn ? y : mn), years[0]);
    const eMax = years.reduce((mx, y) => (y > mx ? y : mx), years[0]);
    return Math.max(1, eMax - eMin);
  }, [entities]);

  // Viewport year bounds — derived from viewState, used for axis labels.
  // Changes on pan/zoom (correct — the axis adapts to what's visible).
  const { minYear, maxYear, yearRange } = useMemo(() => {
    if (viewState.scale <= 0) return { minYear: 0, maxYear: 2000, yearRange: 2000 };
    const minY = (0 - viewState.offsetX) / viewState.scale;
    const maxY = (width - viewState.offsetX) / viewState.scale;
    return { minYear: minY, maxYear: maxY, yearRange: maxY - minY };
  }, [viewState, width]);

  // ResizeObserver for dynamic width
  useEffect(() => {
    if (!containerRef.current) return;
    let rafId: number | null = null;
    const observer = new ResizeObserver((entries) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        for (const entry of entries) {
          const nextWidth = entry.contentRect.width;
          setActualWidth((prev) => (prev !== nextWidth ? nextWidth : prev));
        }
      });
    });
    observer.observe(containerRef.current);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  // yearToX is the identity function: year values ARE world coordinates.
  // Stable (no dependencies) — viewState.scale/offsetX handle all zoom/pan.
  // This prevents coordinate-system shifts when entity data changes.
  const yearToX = useCallback((year: number) => year, []);

  // maxScale based on entity range: controls maximum zoom depth
  const maxScale = useMemo(() => Math.max(BASE_MAX_SCALE, entityYearRange / 10), [entityYearRange]);

  // Calculate a fit-all view centered on entity cluster (5th-95th percentile).
  // With identity yearToX, p5/p95 are year values = world X values directly.
  const calculateFitView = useCallback((): ViewState => {
    if (entities.length === 0) return { offsetX: 0, offsetY: 0, scale: 0.4 };

    const years = entities.map((e) => e.start.year).sort((a, b) => a - b);
    const p5 = years[Math.floor(years.length * 0.05)];
    const p95 = years[Math.min(years.length - 1, Math.floor(years.length * 0.95))];
    const rangeX = Math.max(1, p95 - p5);

    const margin = width * 0.1;
    const targetWidth = width - 2 * margin;
    const scale = Math.max(MIN_SCALE, Math.min(maxScale, targetWidth / rangeX));
    const centerX = (p5 + p95) / 2;
    const offsetX = width / 2 - centerX * scale;

    return { offsetX, offsetY: 0, scale };
  }, [entities, width, maxScale]);

  // Auto-fit view when entities first load (only if no saved state)
  // React "adjusting state during rendering" pattern — runs at most once
  if (entities.length > 0 && !initialized) {
    setInitialized(true);
    const hasSaved = typeof window !== "undefined" && localStorage.getItem(persistKey) !== null;
    if (!hasSaved && !initialViewState) {
      setViewState(calculateFitView());
    }
  }

  // Persist viewState to localStorage (300ms debounce to avoid race with auto-fit)
  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        localStorage.setItem(persistKey, JSON.stringify(viewState));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [viewState, persistKey]);

  // Notify parent of view state changes for URL sync.
  // Uses a ref for the callback to avoid re-triggering on parent re-renders.
  const onViewStateChangeRef = useRef(onViewStateChange);
  useEffect(() => {
    onViewStateChangeRef.current = onViewStateChange;
  }, [onViewStateChange]);

  useEffect(() => {
    if (!onViewStateChangeRef.current) return;
    const timeout = setTimeout(() => {
      if (!onViewStateChangeRef.current || viewState.scale <= 0) return;
      const centerYear = (width / 2 - viewState.offsetX) / viewState.scale;
      onViewStateChangeRef.current(Math.round(centerYear), Math.round(viewState.scale * 1000) / 1000);
    }, VISIBLE_RANGE_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [viewState, width]);

  const prevVisibleRangeRef = useRef<[number, number] | null>(null);

  // With identity yearToX, screen X = year * scale + offsetX.
  // Inverting: year = (screenX - offsetX) / scale.
  const computeVisibleYearRange = useCallback((): [number, number] => {
    if (viewState.scale <= 0) return [0, 2000];
    const minY = (0 - viewState.offsetX) / viewState.scale;
    const maxY = (width - viewState.offsetX) / viewState.scale;
    return [Math.floor(minY), Math.ceil(maxY)];
  }, [viewState, width]);

  useEffect(() => {
    if (!onVisibleYearRangeChange) return;
    const timeout = setTimeout(() => {
      const range = computeVisibleYearRange();
      if (prevVisibleRangeRef.current?.[0] === range[0] && prevVisibleRangeRef.current?.[1] === range[1]) return;
      prevVisibleRangeRef.current = range;
      onVisibleYearRangeChange(range);
    }, VISIBLE_RANGE_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [viewState, onVisibleYearRangeChange, computeVisibleYearRange]);

  // Assign row numbers sequentially
  const entitiesWithPositions = useMemo(() => {
    return entities.map((entity, index) => ({
      ...entity,
      rowNumber: entity.rowNumber ?? index,
    }));
  }, [entities]);

  // Render via extracted function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    renderTimeline({
      ctx,
      width,
      height,
      viewState,
      entities: entitiesWithPositions,
      hoveredEntity,
      mousePos: tooltipPos,
      minYear,
      maxYear,
      yearRange,
      yearToX,
      labels,
    });
  }, [
    entitiesWithPositions,
    width,
    height,
    viewState,
    hoveredEntity,
    tooltipPos,
    minYear,
    maxYear,
    yearRange,
    yearToX,
    labels,
  ]);

  // Re-render canvas when any visual state changes, throttled to browser paint cycle
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      render();
    });
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [render]);

  // Mouse handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const { offsetX, offsetY } = e.nativeEvent as MouseEvent;

      const clickedEntity = entitiesWithPositions.find((entity) => {
        const pos = entityToScreen(entity, yearToX, viewState);
        const hasEndDate = entity.end && entity.end.year !== entity.start.year;

        if (hasEndDate) {
          const startX = yearToX(entity.start.year);
          const endX = yearToX(entity.end?.year ?? entity.start.year);
          const entityWidth = Math.max(MIN_ENTITY_WIDTH * 3, (endX - startX) * viewState.scale);
          const entityHeight = 30;
          return (
            offsetX >= pos.x &&
            offsetX <= pos.x + entityWidth &&
            offsetY >= pos.y - entityHeight / 2 &&
            offsetY <= pos.y + entityHeight / 2
          );
        }
        const dist = Math.sqrt((offsetX - pos.x) ** 2 + (offsetY - pos.y) ** 2);
        if (dist <= 17) return true;
        const textX = pos.x + 17;
        const textWidth = entity.title.length * (TEXT_FONT_PX * 0.6);
        return (
          offsetX >= textX &&
          offsetX <= textX + textWidth &&
          offsetY >= pos.y - TEXT_FONT_PX / 2 &&
          offsetY <= pos.y + TEXT_FONT_PX / 2
        );
      });

      if (clickedEntity) {
        onEntityClick?.(clickedEntity);
        return;
      }

      setIsDragging(true);
      setDragStart({
        x: offsetX - viewState.offsetX,
        y: offsetY - viewState.offsetY,
      });
    },
    [entitiesWithPositions, viewState, onEntityClick, yearToX],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const { offsetX, offsetY } = e.nativeEvent as MouseEvent;

      if (isDragging) {
        setViewState((prev) => ({
          ...prev,
          offsetX: offsetX - dragStart.x,
          offsetY: offsetY - dragStart.y,
        }));
        return;
      }

      const ctx = canvasRef.current?.getContext("2d");
      if (ctx) ctx.font = BOLD_TEXT_FONT;

      const foundEntity = entitiesWithPositions.find((entity) => {
        // Compute Y from rowNumber only (no yearToX call) — cheapest possible cull
        const pointY = ENTITY_START_Y + (entity.rowNumber || 0) * ROW_HEIGHT + viewState.offsetY;
        if (pointY < -50 || pointY > height + 50) return false;

        const screenPos = entityToScreen(entity, yearToX, viewState);
        const hasEndDate = entity.end && entity.end.year !== entity.start.year;

        // X cull for point entities (range entities test their full width below)
        if (!hasEndDate && (screenPos.x < -50 || screenPos.x > width + 50)) return false;

        if (hasEndDate) {
          const toScreenX = (yr: number) => yearToX(yr) * viewState.scale + viewState.offsetX;
          const startXScreen = toScreenX(entity.start.year);
          const endXScreen = toScreenX(entity.end?.year ?? entity.start.year);
          const widthScreen = Math.max(MIN_ENTITY_WIDTH * 3, endXScreen - startXScreen);
          const pillH = 30;
          return (
            offsetX >= startXScreen &&
            offsetX <= startXScreen + widthScreen &&
            offsetY >= pointY - pillH / 2 &&
            offsetY <= pointY + pillH / 2
          );
        }
        const OUTER_R = 12;
        const dist = Math.hypot(offsetX - screenPos.x, offsetY - screenPos.y);
        if (dist <= OUTER_R) return true;
        if (ctx) {
          const textXScreen = screenPos.x + OUTER_R + 5;
          const textW = ctx.measureText(entity.title || "").width;
          const textH = TEXT_FONT_PX + 2;
          return (
            offsetX >= textXScreen &&
            offsetX <= textXScreen + textW &&
            offsetY >= pointY - textH / 2 &&
            offsetY <= pointY + textH / 2
          );
        }
        return false;
      });

      const newHovered = foundEntity || null;
      setHoveredEntity(newHovered);
      if (newHovered) {
        setTooltipPos({ x: offsetX, y: offsetY });
      }
      onEntityHover?.(newHovered);
    },
    [isDragging, dragStart, entitiesWithPositions, viewState, yearToX, onEntityHover, height, width],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Keep maxScale in a ref for the wheel handler (empty-deps effect)
  const maxScaleRef = useRef(maxScale);
  useEffect(() => {
    maxScaleRef.current = maxScale;
  }, [maxScale]);

  // Wheel zoom
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const zoomFactor = e.deltaY > 0 ? 0.97 : 1.03;
      setViewState((prev) => {
        const newScale = Math.max(MIN_SCALE, Math.min(maxScaleRef.current, prev.scale * zoomFactor));
        if (newScale === prev.scale) return prev;
        return {
          ...prev,
          scale: newScale,
          offsetX: mouseX - (mouseX - prev.offsetX) * (newScale / prev.scale),
        };
      });
    };

    canvas.addEventListener("wheel", wheelHandler, { passive: false });
    return () => canvas.removeEventListener("wheel", wheelHandler);
  }, []);

  // Zoom controls
  const zoomTo = useCallback(
    (factor: number) => {
      setViewState((prev) => {
        const newScale = Math.max(MIN_SCALE, Math.min(maxScale, prev.scale * factor));
        const cx = width / 2;
        return {
          ...prev,
          scale: newScale,
          offsetX: cx - (cx - prev.offsetX) * (newScale / prev.scale),
        };
      });
    },
    [width, maxScale],
  );
  const handleZoomIn = useCallback(() => zoomTo(1.2), [zoomTo]);
  const handleZoomOut = useCallback(() => zoomTo(1 / 1.2), [zoomTo]);
  const handleZoomReset = useCallback(() => {
    setViewState(calculateFitView());
    try {
      localStorage.removeItem(persistKey);
    } catch {
      // ignore
    }
  }, [calculateFitView, persistKey]);

  const hasNoEntities = entities.length === 0;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: `${height}px`,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          cursor: isDragging ? "grabbing" : hoveredEntity ? "pointer" : "grab",
          display: "block",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
      <TimelineMinimap
        entities={entitiesWithPositions}
        viewState={viewState}
        mainWidth={width}
        mainHeight={height}
        yearToX={yearToX}
        onNavigate={setViewState}
      />
      <TimelineZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onZoomReset={handleZoomReset} />
      {hasNoEntities && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.3)",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              padding: "16px 24px",
              fontSize: 16,
              fontWeight: 500,
              textAlign: "center",
              backgroundColor: "#f1f5f9",
              borderRadius: "0.75rem",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              color: "#64748b",
            }}
          >
            {t("feature.timeline.noData")}
          </div>
        </div>
      )}
    </div>
  );
};
